import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function formatINR(paise) {
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
}

const PLAN_PERKS = {
  elite_monthly: [
    "Unlimited AI Trade Doctor audits",
    "Full PDF report exports",
    "Priority CSV parsing across all supported brokers",
    "30 days of Elite access (plus a 3-day grace window)",
  ],
};

function PlanCard({ plan, isPro, paying, onPay, configured }) {
  const perks = PLAN_PERKS[plan.plan_id] || [];
  const featured = plan.plan_id === "elite_monthly";
  return (
    <div
      data-testid={`plan-card-${plan.plan_id}`}
      className={`relative flex flex-col rounded-sm border p-6 bg-[var(--bt-surface)] ${
        featured ? "border-[var(--bt-accent)]" : "border-[var(--bt-border)]"
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-6 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] bg-[var(--bt-accent)] text-black rounded-sm">
          Best value
        </span>
      )}
      <p className="text-xs uppercase tracking-[0.2em] text-[var(--bt-text-muted)]">{plan.name}</p>
      <p className="mt-3 font-heading text-4xl text-[var(--bt-text)]" data-testid={`plan-price-${plan.plan_id}`}>
        {formatINR(plan.amount)}
        <span className="text-sm text-[var(--bt-text-muted)] font-normal"> / month</span>
      </p>
      <ul className="mt-5 space-y-2.5 flex-1">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-[var(--bt-text-muted)]">
            <Check className="w-4 h-4 mt-0.5 text-[var(--bt-accent)] flex-none" /> {p}
          </li>
        ))}
      </ul>
      <Button
        data-testid={`plan-pay-btn-${plan.plan_id}`}
        disabled={!configured || paying !== null || isPro}
        onClick={() => onPay(plan)}
        className={`mt-6 w-full font-semibold ${
          featured
            ? "bg-[var(--bt-accent)] text-black hover:bg-[var(--bt-accent-hover)]"
            : "bg-transparent border border-[var(--bt-border)] text-[var(--bt-text)] hover:bg-[var(--bt-surface-hover)]"
        }`}
      >
        {paying === plan.plan_id ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout…</>
        ) : isPro ? "Already Elite" : `Upgrade — ${formatINR(plan.amount)}`}
      </Button>
    </div>
  );
}

function TransactionHistory({ transactions }) {
  if (!transactions?.length) return null;
  return (
    <div data-testid="payment-history" className="mt-10">
      <h2 className="text-lg font-heading text-[var(--bt-text)] mb-3">Payment history</h2>
      <div className="border border-[var(--bt-border)] rounded-sm overflow-hidden">
        {transactions.map((t) => (
          <div
            key={t.id}
            data-testid={`payment-row-${t.id}`}
            className="flex items-center justify-between px-4 py-3 border-b border-[var(--bt-border)] last:border-b-0 bg-[var(--bt-surface)]"
          >
            <div>
              <p className="text-sm text-[var(--bt-text)]">{t.plan_name || t.plan_id}</p>
              <p className="text-xs text-[var(--bt-text-muted)] font-mono">{new Date(t.created_at).toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-mono text-[var(--bt-text)]">{formatINR(t.amount)}</span>
              <span
                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold ${
                  t.status === "paid"
                    ? "bg-[var(--bt-accent)] text-black"
                    : t.status === "failed"
                    ? "bg-[var(--bt-danger)] text-white"
                    : "bg-[var(--bt-surface-hover)] text-[var(--bt-text-muted)]"
                }`}
              >
                {t.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [paying, setPaying] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/payments/config"), api.get("/payments/status")]);
      setConfig(c.data);
      setStatus(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handlePay = async (plan) => {
    setPaying(plan.plan_id);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay checkout. Check your network.");
      const { data } = await api.post("/payments/create-order", { plan_id: plan.plan_id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "BrainTRADES",
        description: plan.name,
        order_id: data.order_id,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#00E5A0" },
        modal: { ondismiss: () => setPaying(null) },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment successful — welcome to Elite!");
            await refresh();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment verification failed");
          } finally {
            setPaying(null);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        // iter132 loose-end — payment retry UX. Sonner action toast
        // with a one-tap "Try again" that re-opens Checkout from a
        // fresh order. Razorpay gives us error.description +
        // error.reason; we surface description if present (already
        // user-friendly) and fall back to a generic line.
        const err = resp?.error || {};
        const reason = err.description || "Something went wrong on the payment provider's end.";
        const code = err.code || err.reason;
        const suffix = code ? ` (${code})` : "";
        toast.error(`Payment didn't go through: ${reason}${suffix}`, {
          duration: 12000,
          action: {
            label: "Try again",
            // Fire a brand-new create-order + Checkout cycle so we
            // don't reuse a spent order_id.
            onClick: () => handlePay(plan),
          },
        });
        setPaying(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="pricing-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--bt-text-muted)]" />
      </div>
    );
  }

  const isPro = !!status?.is_pro;

  return (
    <div data-testid="pricing-page" className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl text-[var(--bt-text)] flex items-center gap-3">
          <Crown className="w-8 h-8 text-[var(--bt-accent)]" /> Go Elite
        </h1>
        <p className="mt-2 text-base text-[var(--bt-text-muted)] max-w-xl">
          Unlock unlimited AI Trade Doctor audits, PDF reports, and priority CSV parsing. Payments are processed securely by Razorpay — UPI, cards, netbanking and wallets supported.
        </p>
      </div>

      {config && !config.configured && (
        <div
          data-testid="payments-not-configured-banner"
          className="mb-6 flex items-start gap-3 border border-[var(--bt-warn)] rounded-sm px-4 py-3 bg-[var(--bt-surface)]"
        >
          <AlertTriangle className="w-5 h-5 text-[var(--bt-warn)] flex-none mt-0.5" />
          <p className="text-sm text-[var(--bt-text-muted)]">
            Payments are not live yet — Razorpay API keys haven't been configured on the server. Add
            <span className="font-mono text-xs"> RAZORPAY_KEY_ID</span> and
            <span className="font-mono text-xs"> RAZORPAY_KEY_SECRET</span> to enable checkout.
          </p>
        </div>
      )}

      {isPro && (
        <div
          data-testid="pro-active-banner"
          className="mb-6 flex items-center gap-3 border border-[var(--bt-accent)] rounded-sm px-4 py-3 bg-[var(--bt-surface)]"
        >
          <ShieldCheck className="w-5 h-5 text-[var(--bt-accent)] flex-none" />
          <p className="text-sm text-[var(--bt-text)]">
            <span className="font-bold">Elite is active</span>
            <span className="text-[var(--bt-text-muted)]"> — until {new Date(status.pro_until).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}. Paying again extends your access.</span>
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
        {(config?.plans || []).map((plan) => (
          <PlanCard
            key={plan.plan_id}
            plan={plan}
            isPro={false}
            paying={paying}
            onPay={handlePay}
            configured={!!config?.configured}
          />
        ))}
      </div>

      <TransactionHistory transactions={status?.transactions} />
    </div>
  );
}
